/**
 * Module-level external store tracking which conversations are currently
 * running. Used by ConversationSidebar to show shimmer indicators and by
 * the per-conversation runtime to coordinate with status polling.
 */
import { useSyncExternalStore } from "react";

let runningSnapshot = new Set<string>();
const runningListeners = new Set<() => void>();

function notifyRunningListeners() {
  runningListeners.forEach((l) => l());
}

export function markConversationRunning(id: string): void {
  if (!runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.add(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

export function markConversationIdle(id: string): void {
  if (runningSnapshot.has(id)) {
    const next = new Set(runningSnapshot);
    next.delete(id);
    runningSnapshot = next;
    notifyRunningListeners();
  }
}

export function subscribeRunning(callback: () => void): () => void {
  runningListeners.add(callback);
  return () => {
    runningListeners.delete(callback);
  };
}

export function getRunningSnapshot(): Set<string> {
  return runningSnapshot;
}

/**
 * Reactive hook returning the set of conversation IDs with active runs.
 * Re-renders only when the set changes.
 */
export function useRunningConversations(): Set<string> {
  return useSyncExternalStore(subscribeRunning, getRunningSnapshot, getRunningSnapshot);
}
